import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api, fileUrl } from "@/api";
import { useAuth } from "@/auth";
import { Card, Spinner } from "@/components/ui-kit";
import { CreatorProfileView } from "@/components/CreatorProfileView";
import { ProBadge } from "@/components/dashboard-bits";
import { Users, UserRound, Building2, TrendingUp, Megaphone, MessageSquare, LogOut, UserPlus, Activity, Newspaper, Sparkles, X } from "lucide-react";

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <Card className="p-6" data-testid={testid}>
      <div className="flex items-center gap-2 text-[#968F85]">
        <Icon size={16} strokeWidth={1.75} />
        <span className="text-[13px]">{label}</span>
      </div>
      <div className="mt-3 text-3xl font-semibold tracking-display text-[#292827]" data-testid={`${testid}-value`}>{value}</div>
    </Card>
  );
}

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("fi-FI", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return ""; }
}

const ROLE_LABEL = { creator: "Sisällöntuottaja", business: "Yritys", admin: "Ylläpitäjä" };
const TYPE_LABEL = { registration: "Rekisteröityminen", campaign: "Kampanjan julkaisu" };

export default function AdminDashboard() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(undefined);
  const [subs, setSubs] = useState(null);
  const [proInterest, setProInterest] = useState(null);
  const [creators, setCreators] = useState(null);
  const [businesses, setBusinesses] = useState(null);
  const [campaignList, setCampaignList] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/admin/overview").then((r) => setData(r.data)).catch(() => setData(null));
    api.get("/admin/newsletter-subscribers").then((r) => setSubs(r.data)).catch(() => setSubs(null));
    api.get("/admin/pro-interest").then((r) => setProInterest(r.data)).catch(() => setProInterest(null));
    api.get("/admin/creators").then((r) => setCreators(r.data)).catch(() => setCreators(null));
    api.get("/admin/businesses").then((r) => setBusinesses(r.data)).catch(() => setBusinesses(null));
    api.get("/admin/campaigns").then((r) => setCampaignList(r.data)).catch(() => setCampaignList(null));
  }, []);

  const openCreator = async (id) => {
    try {
      const { data } = await api.get(`/admin/creators/${id}`);
      setSelected(data);
    } catch { setSelected(null); }
  };

  const doLogout = async () => { await logout(); navigate("/", { replace: true }); };

  if (data === undefined) return <div className="min-h-screen flex items-center justify-center"><Spinner /></div>;

  const u = data?.users || {};
  const c = data?.content || {};
  const chartData = (data?.daily_registrations || []).map((d) => ({
    date: d.date.slice(5).replace("-", "."), count: d.count,
  }));

  return (
    <div className="min-h-screen bg-[#F5F9FE]" data-testid="admin-dashboard">
      <div className="mx-auto max-w-6xl px-6 py-10">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-[13px] text-[#968F85]">Ylläpito · beta</span>
            <h1 className="text-3xl font-semibold tracking-display text-[#292827]">Creatori Dashboard</h1>
          </div>
          <button onClick={doLogout} data-testid="admin-logout"
            className="inline-flex items-center gap-2 rounded-full border border-[#E6E1D9] bg-white px-5 py-2.5 text-sm font-medium text-[#292827] hover:border-[#292827] transition-colors">
            <LogOut size={17} strokeWidth={1.75} /> Kirjaudu ulos
          </button>
        </div>

        {data === null ? (
          <Card className="mt-8 p-8 text-[15px] text-[#6E675C]">Tietojen lataaminen epäonnistui.</Card>
        ) : (
          <>
            <h2 className="mt-10 mb-4 text-sm font-medium uppercase tracking-wide text-[#968F85]">Käyttäjät</h2>
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
              <Stat icon={Users} label="Käyttäjiä yhteensä" value={u.total ?? 0} testid="stat-users-total" />
              <Stat icon={UserRound} label="Sisällöntuottajia" value={u.creators ?? 0} testid="stat-creators" />
              <Stat icon={Building2} label="Yrityksiä" value={u.businesses ?? 0} testid="stat-businesses" />
              <Stat icon={Activity} label="Aktiiviset (7 pv)" value={u.active_7d ?? 0} testid="stat-active-7d" />
              <Stat icon={TrendingUp} label="Uudet (7 pv)" value={u.new_7d ?? 0} testid="stat-new-7d" />
              <Stat icon={TrendingUp} label="Uudet (30 pv)" value={u.new_30d ?? 0} testid="stat-new-30d" />
            </div>

            <h2 className="mt-10 mb-4 text-sm font-medium uppercase tracking-wide text-[#968F85]">Sisältö</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Stat icon={Megaphone} label="Kampanjoita yhteensä" value={c.campaigns_total ?? 0} testid="stat-campaigns-total" />
              <Stat icon={Megaphone} label="Avoimia kampanjoita" value={c.campaigns_open ?? 0} testid="stat-campaigns-open" />
              <Stat icon={MessageSquare} label="Keskusteluja" value={c.conversations_total ?? 0} testid="stat-conversations" />
              <Stat icon={MessageSquare} label="Viestejä" value={c.messages_total ?? 0} testid="stat-messages" />
            </div>

            <div className="mt-10 grid lg:grid-cols-2 gap-6">
              <Card className="p-7" data-testid="admin-chart">
                <div className="flex items-center gap-2 text-sm font-medium text-[#292827]"><UserPlus size={16} strokeWidth={1.75} /> Päivittäiset rekisteröitymiset (14 pv)</div>
                <div className="mt-6 h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EDECEA" vertical={false} />
                      <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#968F85" }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#968F85" }} axisLine={false} tickLine={false} width={40} />
                      <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E6E1D9", fontSize: 13 }} labelStyle={{ color: "#292827" }} />
                      <Line type="monotone" dataKey="count" stroke="#292827" strokeWidth={2} dot={{ r: 3, fill: "#292827" }} activeDot={{ r: 5 }} name="Rekisteröitymiset" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              <Card className="p-7" data-testid="admin-recent-events">
                <div className="text-sm font-medium text-[#292827]">Viimeisimmät tapahtumat</div>
                <div className="mt-5 divide-y divide-[#EDECEA]">
                  {(data.recent_events || []).map((e, i) => (
                    <div key={i} className="flex items-center justify-between gap-3 py-3" data-testid="admin-event-row">
                      <div className="min-w-0">
                        <div className="text-[14px] font-medium text-[#292827] truncate">{TYPE_LABEL[e.type] || e.type}</div>
                        <div className="text-[12px] text-[#968F85] truncate">{e.label} · {ROLE_LABEL[e.role] || e.role}</div>
                      </div>
                      <span className="text-[12px] text-[#968F85] shrink-0">{fmtDate(e.date)}</span>
                    </div>
                  ))}
                  {(!data.recent_events || data.recent_events.length === 0) && (
                    <div className="py-6 text-[14px] text-[#968F85]">Ei tapahtumia vielä.</div>
                  )}
                </div>
              </Card>
            </div>

            <Card className="mt-6 p-7" data-testid="admin-newsletter">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-sm font-medium text-[#292827]"><Newspaper size={16} strokeWidth={1.75} /> Uutiskirjeen tilaajat</div>
                <span className="text-2xl font-semibold tracking-display text-[#292827]" data-testid="newsletter-total">{subs?.total ?? (u.newsletter_subscribers ?? 0)}</span>
              </div>
              {subs && subs.subscribers.length > 0 ? (
                <div className="mt-5 max-h-72 overflow-y-auto divide-y divide-[#EDECEA]">
                  {subs.subscribers.map((s, i) => (
                    <div key={i} className="flex items-center justify-between gap-3 py-3" data-testid="newsletter-row">
                      <div className="min-w-0">
                        <div className="text-[14px] font-medium text-[#292827] truncate">{s.name}</div>
                        <div className="text-[12px] text-[#968F85] truncate">{s.email} · {ROLE_LABEL[s.role] || s.role}</div>
                      </div>
                      <span className="text-[12px] text-[#968F85] shrink-0">{fmtDate(s.opted_in_at)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-4 text-[14px] text-[#968F85]">Ei tilaajia vielä.</div>
              )}
            </Card>

            <Card className="mt-6 p-7" data-testid="admin-pro-interest">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-sm font-medium text-[#292827]"><Sparkles size={16} strokeWidth={1.75} /> Pro-tilauksesta kiinnostuneet</div>
                <span className="text-2xl font-semibold tracking-display text-[#292827]" data-testid="pro-interest-total">{proInterest?.total ?? 0}</span>
              </div>
              <div className="mt-5 grid sm:grid-cols-2 gap-6">
                <div data-testid="pro-interest-creators">
                  <div className="flex items-center gap-2 text-[13px] font-medium text-[#968F85]"><UserRound size={14} strokeWidth={1.75} /> Sisällöntuottajat ({proInterest?.creators?.length ?? 0})</div>
                  {proInterest && proInterest.creators.length > 0 ? (
                    <div className="mt-3 max-h-72 overflow-y-auto divide-y divide-[#EDECEA]">
                      {proInterest.creators.map((p, i) => (
                        <div key={i} className="py-3" data-testid="pro-interest-creator-row">
                          <div className="text-[14px] font-medium text-[#292827] truncate">{p.name}</div>
                          <div className="text-[12px] text-[#968F85] truncate">{p.email}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-3 text-[14px] text-[#968F85]">Ei vielä kiinnostuneita.</div>
                  )}
                </div>
                <div data-testid="pro-interest-businesses">
                  <div className="flex items-center gap-2 text-[13px] font-medium text-[#968F85]"><Building2 size={14} strokeWidth={1.75} /> Yritykset ({proInterest?.businesses?.length ?? 0})</div>
                  {proInterest && proInterest.businesses.length > 0 ? (
                    <div className="mt-3 max-h-72 overflow-y-auto divide-y divide-[#EDECEA]">
                      {proInterest.businesses.map((p, i) => (
                        <div key={i} className="py-3" data-testid="pro-interest-business-row">
                          <div className="text-[14px] font-medium text-[#292827] truncate">{p.company_name}</div>
                          <div className="text-[12px] text-[#968F85] truncate">{p.email}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-3 text-[14px] text-[#968F85]">Ei vielä kiinnostuneita.</div>
                  )}
                </div>
              </div>
            </Card>

            <Card className="mt-6 p-7" data-testid="admin-creators">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-sm font-medium text-[#292827]"><UserRound size={16} strokeWidth={1.75} /> Sisällöntuottajien profiilit</div>
                <span className="text-2xl font-semibold tracking-display text-[#292827]" data-testid="admin-creators-total">{creators?.total ?? 0}</span>
              </div>
              <p className="mt-1 text-[13px] text-[#968F85]">Avaa creatorin profiili klikkaamalla nimeä.</p>
              {creators && creators.creators.length > 0 ? (
                <div className="mt-5 max-h-96 overflow-y-auto divide-y divide-[#EDECEA]">
                  {creators.creators.map((c) => (
                    <button key={c.user_id} onClick={() => openCreator(c.user_id)} data-testid="admin-creator-row"
                      className="w-full flex items-center gap-3 py-3 text-left rounded-lg px-2 -mx-2 hover:bg-[#F5F3F0] transition-colors">
                      <div className="h-10 w-10 rounded-full overflow-hidden bg-[#E9EAEC] flex items-center justify-center text-[12px] font-medium text-[#292827] shrink-0">
                        {c.profile_photo ? <img src={fileUrl(c.profile_photo)} alt="" className="h-full w-full object-cover" /> : (c.name || "?").slice(0, 2).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[14px] font-medium text-[#292827] truncate flex items-center gap-2" data-testid="admin-creator-name">
                          {c.name || "—"} {c.is_pro && <ProBadge />}
                        </div>
                        <div className="text-[12px] text-[#968F85] truncate">{c.email}{c.location ? ` · ${c.location}` : ""}</div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="mt-4 text-[14px] text-[#968F85]">Ei sisällöntuottajia vielä.</div>
              )}
            </Card>

            <Card className="mt-6 p-7" data-testid="admin-businesses">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-sm font-medium text-[#292827]"><Building2 size={16} strokeWidth={1.75} /> Yritysten profiilit</div>
                <span className="text-2xl font-semibold tracking-display text-[#292827]" data-testid="admin-businesses-total">{businesses?.total ?? 0}</span>
              </div>
              <p className="mt-1 text-[13px] text-[#968F85]">Kaikki rekisteröityneet yritykset.</p>
              {businesses && businesses.businesses.length > 0 ? (
                <div className="mt-5 max-h-96 overflow-y-auto divide-y divide-[#EDECEA]">
                  {businesses.businesses.map((b) => (
                    <div key={b.user_id} data-testid="admin-business-row" className="flex items-center gap-3 py-3">
                      <div className="h-10 w-10 rounded-full overflow-hidden bg-[#E9EAEC] flex items-center justify-center text-[12px] font-medium text-[#292827] shrink-0">
                        {b.logo ? <img src={fileUrl(b.logo)} alt="" className="h-full w-full object-contain p-1" /> : (b.company_name || "?").slice(0, 2).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[14px] font-medium text-[#292827] truncate flex items-center gap-2" data-testid="admin-business-name">
                          {b.company_name || "—"} {b.is_pro && <ProBadge />}
                        </div>
                        <div className="text-[12px] text-[#968F85] truncate">{b.email}{b.industry ? ` · ${b.industry}` : ""}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-4 text-[14px] text-[#968F85]">Ei yrityksiä vielä.</div>
              )}
            </Card>

            <Card className="mt-6 p-7" data-testid="admin-campaigns">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-sm font-medium text-[#292827]"><Megaphone size={16} strokeWidth={1.75} /> Kampanjat</div>
                <span className="text-2xl font-semibold tracking-display text-[#292827]" data-testid="admin-campaigns-total">{campaignList?.total ?? 0}</span>
              </div>
              <p className="mt-1 text-[13px] text-[#968F85]">Lähetettyjen hakemusten ja aloitettujen keskustelujen määrä kampanjaa kohti.</p>
              {campaignList && campaignList.campaigns.length > 0 ? (
                <div className="mt-5 max-h-96 overflow-y-auto divide-y divide-[#EDECEA]">
                  {campaignList.campaigns.map((c) => (
                    <div key={c.campaign_id} className="flex items-center gap-3 py-3" data-testid="admin-campaign-row">
                      <div className="min-w-0 flex-1">
                        <div className="text-[14px] font-medium text-[#292827] truncate" data-testid="admin-campaign-title">{c.title || "—"}</div>
                        <div className="text-[12px] text-[#968F85] truncate">{c.business_name}{c.status ? ` · ${c.status === "open" ? "avoin" : c.status === "draft" ? "luonnos" : "suljettu"}` : ""}</div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="rounded-full bg-[#F5F3F0] px-3 py-1 text-[13px] font-medium text-[#292827]" data-testid="admin-campaign-applications">
                          {c.application_count} {c.application_count === 1 ? "hakemus" : "hakemusta"}
                        </span>
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#DCE6F5] px-3 py-1 text-[13px] font-medium text-[#292827]" data-testid="admin-campaign-conversations">
                          <MessageSquare size={13} strokeWidth={2} /> {c.conversation_count ?? 0} {(c.conversation_count ?? 0) === 1 ? "keskustelu" : "keskustelua"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-4 text-[14px] text-[#968F85]">Ei kampanjoita vielä.</div>
              )}
            </Card>
          </>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:p-8"
          data-testid="admin-creator-modal" onClick={() => setSelected(null)}>
          <div className="relative w-full max-w-3xl my-2" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-white/90">Sisällöntuottajan profiili</span>
              <button onClick={() => setSelected(null)} data-testid="admin-creator-close"
                className="h-9 w-9 rounded-full bg-white flex items-center justify-center text-[#292827] hover:bg-white/90 transition-colors"><X size={18} /></button>
            </div>
            <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-2xl">
              <CreatorProfileView creator={selected} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
